import GenericDialogue from 'src/components/Dialogue/Dialogue';
import { useEffect } from 'react';
import BulkAddWalkthroughsDialogueSkeleton from 'src/components/BulkOperations/Walkthroughs/BulkAddWalkthroughsDialogue/skeletons/BulkAddWalkthroughsDialogueSkeleton';
import Button from 'src/components/Button';
import BulkAddWalkthroughs from 'src/components/BulkOperations/Walkthroughs/BulkAddWalkthroughs/BulkAddWalkthroughs';
import BulkWalkthroughsSummary from 'src/components/BulkOperations/Walkthroughs/BulkWalkthroughSummary/BulKWalkthroughSummary';
import {
  getWalkthroughSummaryStats,
  IWalkthroughSummaryStats,
} from 'src/components/BulkOperations/Walkthroughs/BulkWalkthroughSummary/walkthroughSummaryStats';
import WalkthroughSummaryHeaderContent from 'src/components/BulkOperations/Walkthroughs/WalkthroughSummaryHeaderContent/WalkthroughSummaryHeaderContent';
import {
  usePropertySuitesBulkDialogues,
  UsePropertySuitesReturn,
} from 'src/SharedCustomHooks/UsePropertySuites/usePropertySuitesBulkDialogues';
import {
  IUseCalcomConnection,
  useCalcomOauthConnection,
} from 'src/pages/Calendar/CalcomIntegration/CustomHooks/useCalcomOauthConnection';
import { IWalkthroughModel } from 'src/services/client-types';
import { variables } from 'src/Tenants';

interface IBulkAddWalkthroughsDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  selectedSuiteIds: string[];
  onSave?: (params: any) => void;
  propertyId: any;
  isLoading: any;
  bulkWalkthroughsCreated: IWalkthroughModel[];
  isBulkCreationSuccess: any;
  onSetBulkSuccess: () => void;
}

const BulkAddWalkthroughsDialogue = ({
  isOpen,
  onClose,
  userId,
  selectedSuiteIds,
  onSave,
  propertyId,
  isLoading,
  bulkWalkthroughsCreated,
  isBulkCreationSuccess,
  onSetBulkSuccess,
}: IBulkAddWalkthroughsDialogueProps) => {
  const {
    simpleSuitesForWalkthroughDialogue,
    isLoading: suitesLoading,
    error: suitesError,
    refetch: refetchSuites,
  }: UsePropertySuitesReturn = usePropertySuitesBulkDialogues({
    propertyId,
    userId,
    enabled: isOpen,
  });

  const {
    isConnected: isCalcomConnected,
    connectedCalendars,
  }: IUseCalcomConnection = useCalcomOauthConnection();

  useEffect(() => {
    if (!isOpen) {
      onSetBulkSuccess();
    }
  }, [isOpen]);

  const handleClose = () => {
    onSetBulkSuccess();
    onClose();
  };

  const handleRetry = () => {
    refetchSuites();
  };

  const suiteName: string = variables.suiteName;
  const dialogueMinHeight: string = '80vh';

  const summaryStats: IWalkthroughSummaryStats | null = isBulkCreationSuccess
    ? getWalkthroughSummaryStats(bulkWalkthroughsCreated)
    : null;

  if (suitesLoading) {
    return (
      <GenericDialogue
        isOpen={isOpen}
        onClose={handleClose}
        title="Bulk Add Walkthroughs"
        description={`Add walkthroughs to multiple ${suiteName} at once`}
        minHeight={dialogueMinHeight}
      >
        <BulkAddWalkthroughsDialogueSkeleton />
      </GenericDialogue>
    );
  }

  if (suitesError) {
    return (
      <GenericDialogue
        isOpen={isOpen}
        onClose={handleClose}
        title="Bulk Add Walkthroughs"
        description={`Add walkthroughs to multiple ${suiteName} at once`}
        minHeight={dialogueMinHeight}
      >
        <div className="bulk-add-walkthroughs__error">
          <p>Error: {suitesError}</p>
          <Button
            color="teal"
            size="small"
            label="Retry"
            onClick={handleRetry}
          />
        </div>
      </GenericDialogue>
    );
  }

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={handleClose}
      title={
        isBulkCreationSuccess ? 'Walkthroughs Created' : 'Bulk Add Walkthroughs'
      }
      description={
        isBulkCreationSuccess
          ? 'Summary of created walkthroughs'
          : `Add walkthroughs to multiple ${suiteName} at once`
      }
      minHeight={dialogueMinHeight}
      subheader={
        summaryStats ? (
          <WalkthroughSummaryHeaderContent {...summaryStats} mode="creation" />
        ) : undefined
      }
      footer={
        isBulkCreationSuccess ? (
          <Button color="teal" size="small" label="Done" onClick={handleClose} />
        ) : undefined
      }
    >
      {isBulkCreationSuccess ? (
        <BulkWalkthroughsSummary
          walkthroughs={bulkWalkthroughsCreated}
          isCalcomConnected={isCalcomConnected}
        />
      ) : (
        <BulkAddWalkthroughs
          userId={userId}
          selectedSuiteIds={selectedSuiteIds}
          onSave={onSave}
          suites={simpleSuitesForWalkthroughDialogue}
          isSubmitting={isLoading}
          propertyId={propertyId}
          isCalcomConnected={isCalcomConnected}
          connectedCalendars={connectedCalendars}
        />
      )}
    </GenericDialogue>
  );
};

export default BulkAddWalkthroughsDialogue;
